import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, CheckCircle2 } from 'lucide-react';

const servicesList = [
  { id: 'call-handling',       label: 'Call Handling' },
  { id: 'virtual-receptionist', label: 'Virtual Receptionist' },
  { id: 'email-monitoring',    label: 'Email Monitoring' },
  { id: 'duty-of-care',        label: 'Duty of Care Calls' },
];

const initialForm = {
  fullName: '', businessName: '', email: '', phone: '',
  businessType: '', services: [], volume: '', message: '',
};

const validate = (form) => {
  const e = {};
  if (!form.fullName.trim())     e.fullName = 'Name is required.';
  if (!form.businessName.trim()) e.businessName = 'Business name is required.';
  if (!form.email.trim())        e.email = 'Email is required.';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email.';
  if (!form.phone.trim())        e.phone = 'Phone number is required.';
  if (!form.businessType)        e.businessType = 'Please select an industry.';
  if (!form.services.length)     e.services = 'Please select at least one service.';
  if (!form.volume)              e.volume = 'Please select estimated volume.';
  return e;
};

const fieldClass = (error) =>
  `w-full rounded-lg border px-4 py-2.5 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-cygnet-dark text-sm ${error ? 'border-red-400' : 'border-gray-300'}`;

const labelClass = 'block text-sm font-medium text-gray-900 mb-1';

const WEB3FORMS_KEY = 'fa4fbee4-d9b0-40d7-a176-172cefe6424a';

const ContactPage = () => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const toggleService = (id) => {
    setForm(prev => ({
      ...prev,
      services: prev.services.includes(id)
        ? prev.services.filter(s => s !== id)
        : [...prev.services, id],
    }));
    setErrors(prev => ({ ...prev, services: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    setSubmitError('');

    try {
      const payload = {
        access_key: WEB3FORMS_KEY,
        subject: `New enquiry from ${form.fullName} — ${form.businessName}`,
        from_name: form.fullName,
        name: form.fullName,
        'Business Name': form.businessName,
        email: form.email,
        'Phone': form.phone,
        'Industry': form.businessType,
        'Services Required': form.services.join(', '),
        'Estimated Monthly Calls': form.volume,
        'Message': form.message || 'No additional message.',
      };

      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
      } else {
        setSubmitError('Something went wrong. Please try again or call us directly.');
      }
    } catch {
      setSubmitError('Network error. Please try again or call us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen pb-12">
      <div className="mx-auto max-w-7xl px-6 py-16 md:py-24 lg:px-8">

        <div className="max-w-3xl mx-auto text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Get in Touch</h1>
          <p className="text-xl text-gray-500 mb-4">
            Fill out the form below and our team will design a custom package based on your exact volume and requirements.
          </p>
          <div className="inline-block bg-cygnet-dark/10 text-cygnet-dark border border-cygnet-dark/20 rounded-lg px-4 py-2 text-sm font-medium">
            We don't do sales calls. If you enquire, we'll have a friendly conversation about your needs — no pressure.
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 max-w-6xl mx-auto">

          {/* Form */}
          <div className="lg:col-span-2 order-2 lg:order-1 bg-white border border-gray-200 rounded-3xl p-6 md:p-10 shadow-sm">
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-3xl mb-4">✓</div>
                <h3 className="text-xl font-semibold text-gray-900">Request sent!</h3>
                <p className="mt-2 text-gray-500">Thank you. Our team will be in touch within 2 hours.</p>
                <button
                  onClick={() => { setSubmitted(false); setForm(initialForm); }}
                  className="mt-6 text-sm text-cygnet-dark hover:underline"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={labelClass}>Full Name</label>
                    <input name="fullName" value={form.fullName} onChange={handleChange} placeholder="Jane Doe" className={fieldClass(errors.fullName)} />
                    {errors.fullName && <p className="mt-1 text-xs text-red-500">{errors.fullName}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>Business Name</label>
                    <input name="businessName" value={form.businessName} onChange={handleChange} placeholder="Acme Ltd" className={fieldClass(errors.businessName)} />
                    {errors.businessName && <p className="mt-1 text-xs text-red-500">{errors.businessName}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={labelClass}>Email Address</label>
                    <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="jane@example.com" className={fieldClass(errors.email)} />
                    {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>Phone Number</label>
                    <input name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="0208 538 5400" className={fieldClass(errors.phone)} />
                    {errors.phone && <p className="mt-1 text-xs text-red-500">{errors.phone}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={labelClass}>Industry / Business Type</label>
                    <select name="businessType" value={form.businessType} onChange={handleChange} className={fieldClass(errors.businessType)}>
                      <option value="">Select industry...</option>
                      <option value="healthcare">Healthcare & Medical</option>
                      <option value="trades">Trades & Engineering</option>
                      <option value="logistics">Transport & Logistics</option>
                      <option value="professional">Professional Services</option>
                      <option value="it">IT & Technology</option>
                      <option value="finance">Finance</option>
                      <option value="retail">Retail</option>
                      <option value="manufacturing">Manufacturing</option>
                      <option value="other">Other</option>
                    </select>
                    {errors.businessType && <p className="mt-1 text-xs text-red-500">{errors.businessType}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>Estimated Monthly Calls</label>
                    <select name="volume" value={form.volume} onChange={handleChange} className={fieldClass(errors.volume)}>
                      <option value="">Select volume...</option>
                      <option value="under-50">Under 50</option>
                      <option value="50-200">50 – 200</option>
                      <option value="200-500">200 – 500</option>
                      <option value="500-plus">500+</option>
                    </select>
                    {errors.volume && <p className="mt-1 text-xs text-red-500">{errors.volume}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-3">Services Required</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {servicesList.map((s) => (
                      <label
                        key={s.id}
                        className={`flex items-center gap-3 rounded-lg border p-4 cursor-pointer hover:bg-gray-50 transition-colors ${form.services.includes(s.id) ? 'border-cygnet-dark bg-cygnet-dark/5' : 'border-gray-200'}`}
                      >
                        <input
                          type="checkbox"
                          checked={form.services.includes(s.id)}
                          onChange={() => toggleService(s.id)}
                          className="h-4 w-4 rounded border-gray-300 text-cygnet-dark focus:ring-cygnet-dark"
                        />
                        <span className="text-sm font-medium text-gray-900">{s.label}</span>
                      </label>
                    ))}
                  </div>
                  {errors.services && <p className="mt-1 text-xs text-red-500">{errors.services}</p>}
                </div>

                <div>
                  <label className={labelClass}>Additional Information (Optional)</label>
                  <textarea name="message" rows={4} value={form.message} onChange={handleChange} placeholder="Tell us a bit more about your specific needs..." className={fieldClass(false)} />
                </div>

                {submitError && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{submitError}</p>
                )}

                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: loading ? 1 : 1.01 }}
                  whileTap={{ scale: loading ? 1 : 0.99 }}
                  className="w-full rounded-md bg-cygnet-dark px-5 py-4 text-base font-semibold text-white shadow-sm hover:bg-cygnet-DEFAULT transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? 'Sending…' : 'Get in Touch'}
                </motion.button>
              </form>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-8 order-1 lg:order-2">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-slate-900 text-white rounded-3xl p-8 shadow-lg"
            >
              <h3 className="text-2xl font-bold mb-6">Contact Details</h3>
              <div className="space-y-6">
                {[
                  { icon: Phone, label: 'Call us directly', value: '0208 538 5400', href: 'tel:+442085385400' },
                  { icon: Mail,  label: 'Email us', value: 'info@cygnetcall.co.uk', href: 'mailto:info@cygnetcall.co.uk' },
                  { icon: MapPin, label: 'Office', value: 'UK-based operations centre', href: null },
                ].map(({ icon: Icon, label, value, href }) => (
                  <div key={label} className="flex items-start gap-4">
                    <div className="bg-cygnet-dark/40 text-white p-3 rounded-xl shrink-0">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-sm text-white/50 font-medium mb-1">{label}</div>
                      {href
                        ? <a href={href} className="font-semibold text-lg hover:text-cygnet-light transition-colors">{value}</a>
                        : <div className="font-semibold">{value}</div>
                      }
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white border border-gray-200 rounded-3xl p-8 shadow-sm"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">Our Promise</h3>
              <ul className="space-y-4">
                {[
                  'We typically respond within 2 hours.',
                  'No obligation, totally free consultation.',
                  'No pushy sales tactics. We quote based on your needs.',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-gray-500">
                    <CheckCircle2 className="w-5 h-5 text-cygnet-dark shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ContactPage;
